import type { User } from '@open-dataroom/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Users,
  Mail,
  UserPlus,
  Trash2,
  UserX,
  UserCheck,
  RotateCcw
} from 'lucide-react';
import * as React from 'react';

import { InviteRoomViewerModal } from '~/components/invite-room-viewer-modal';
import { InviteStaffModal } from '~/components/invite-staff-modal';
import { Avatar, AvatarFallback } from '~/components/ui/avatar';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { Toast } from '~/components/ui/toast';
import { Tooltip } from '~/components/ui/tooltip';
import { api, ApiError, type ListedUser, type UsersResponse } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { isInviteExpired, isPendingInvitation } from '~/lib/invites';
import { requireOwnerOrAdmin } from '~/lib/route-auth';
import { displayNameFromEmail } from '~/lib/user-display';

type MemberFilter = 'active' | 'all';

export const Route = createFileRoute('/settings/users')({
  beforeLoad: async () => {
    await requireOwnerOrAdmin();
  },
  component: UsersSettingsPage
});

interface InviteRow {
  id: string;
  roomId: string;
  groupId: string | null;
  email: string;
  targetRole: 'owner' | 'admin' | null;
  expiresAt: string;
  usedAt: string | null;
  createdBy: string;
  createdAt: string;
}

interface InvitesResponse {
  invites: InviteRow[];
}

interface GroupRow {
  id: string;
  name: string;
}

function initialsFromEmail(email: string): string {
  const name = displayNameFromEmail(email);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  return initials || email.slice(0, 2).toUpperCase();
}

function roleLabel(role: User['role']): string {
  return role[0]!.toUpperCase() + role.slice(1);
}

function InviteTypeBadge({
  invite,
  groupNameById
}: {
  invite: InviteRow;
  groupNameById: Map<string, string>;
}) {
  if (invite.targetRole) {
    return <Badge variant="outline">{roleLabel(invite.targetRole)}</Badge>;
  }

  if (invite.groupId) {
    const groupName = groupNameById.get(invite.groupId);
    const label = groupName ?? 'Group access';
    const badge = (
      <Badge variant="outline" className="max-w-[12rem] truncate">
        {label}
      </Badge>
    );
    if (groupName) {
      return <Tooltip content={`Group: ${groupName}`}>{badge}</Tooltip>;
    }
    return <Tooltip content="Group no longer exists">{badge}</Tooltip>;
  }

  if (invite.roomId) {
    return <Badge variant="outline">Room access</Badge>;
  }

  return <Badge variant="outline">Invite</Badge>;
}

function formatDateTime(value: string): { date: string; time: string } | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString(undefined, { dateStyle: 'medium' }),
    time: date.toLocaleTimeString(undefined, { timeStyle: 'short' })
  };
}

function UserRoleControl({
  listedUser,
  isOwner,
  currentUserId,
  archived,
  updateRolePending,
  onRoleChange
}: {
  listedUser: ListedUser;
  isOwner: boolean;
  currentUserId: string | undefined;
  archived: boolean;
  updateRolePending: boolean;
  onRoleChange: (role: User['role']) => void;
}) {
  if (isOwner) {
    return (
      <Select
        value={listedUser.role}
        disabled={
          updateRolePending || listedUser.id === currentUserId || archived
        }
        onValueChange={(value) => {
          const role = value as User['role'];
          if (role === listedUser.role) return;
          onRoleChange(role);
        }}
      >
        <SelectTrigger className="h-8 w-[8.5rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          <SelectGroup>
            <SelectItem value="viewer">Viewer</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="owner">Owner</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  }

  return <Badge variant="outline">{roleLabel(listedUser.role)}</Badge>;
}

function UserStatusControl({
  listedUser,
  currentUserId,
  archived,
  deactivatePending,
  onReactivate,
  onDeactivate
}: {
  listedUser: ListedUser;
  currentUserId: string | undefined;
  archived: boolean;
  deactivatePending: boolean;
  onReactivate: () => void;
  onDeactivate: () => void;
}) {
  if (listedUser.id === currentUserId) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  if (archived) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 gap-1.5 whitespace-nowrap"
        disabled={deactivatePending}
        onClick={onReactivate}
      >
        <UserCheck className="h-4 w-4" />
        Reactivate
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="shrink-0 gap-1.5 whitespace-nowrap text-destructive hover:text-destructive"
      disabled={deactivatePending}
      onClick={onDeactivate}
    >
      <UserX className="h-4 w-4" />
      Deactivate
    </Button>
  );
}

function UsersSettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOwner = user?.role === 'owner';
  const canInviteToRoom = user?.role === 'owner' || user?.role === 'admin';

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UsersResponse>('/api/users')
  });

  const { data: invitesData, isLoading: invitesLoading } = useQuery({
    queryKey: ['invites'],
    queryFn: () => api.get<InvitesResponse>('/api/invites')
  });

  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<{ groups: GroupRow[] }>('/api/groups')
  });

  const [memberFilter, setMemberFilter] =
    React.useState<MemberFilter>('active');
  const [inviteStaffOpen, setInviteStaffOpen] = React.useState(false);
  const [inviteRoomOpen, setInviteRoomOpen] = React.useState(false);
  const [roleErrorToast, setRoleErrorToast] = React.useState<string | null>(
    null
  );
  const [inviteToast, setInviteToast] = React.useState<{
    title: string;
    description?: string;
    variant?: 'default' | 'destructive';
  } | null>(null);

  const formatApiError = (err: unknown): string => {
    if (err instanceof ApiError) {
      // API errors sometimes return JSON as plain text; prefer the `error` field when present.
      try {
        const parsed = JSON.parse(err.message) as unknown;
        if (
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed) &&
          'error' in parsed &&
          typeof (parsed as { error: unknown }).error === 'string'
        ) {
          return (parsed as { error: string }).error;
        }
      } catch {
        // ignore parse errors
      }
      return err.message;
    }
    return err instanceof Error ? err.message : 'Request failed';
  };

  const updateRole = useMutation({
    mutationFn: (args: { userId: string; role: User['role'] }) =>
      api.patch<{ user: ListedUser }>(`/api/users/${args.userId}`, {
        role: args.role
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      setRoleErrorToast(formatApiError(err));
    }
  });

  const setDeactivated = useMutation({
    mutationFn: (args: { userId: string; deactivated: boolean }) =>
      api.patch<{ user: ListedUser }>(`/api/users/${args.userId}`, {
        deactivated: args.deactivated
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      setRoleErrorToast(formatApiError(err));
    }
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) =>
      api.delete<{ success: true }>(`/api/invites/${inviteId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: unknown) => {
      setRoleErrorToast(formatApiError(err));
    }
  });

  const resendInvite = useMutation({
    mutationFn: (inviteId: string) =>
      api.post<{ invite: InviteRow }>(`/api/invites/${inviteId}/resend`),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      setInviteToast({
        title: 'Invitation resent',
        description: `A new email was sent to ${data.invite.email}. Expiry extended by 7 days.`
      });
    },
    onError: (err: unknown) => {
      setInviteToast({
        title: 'Couldn’t resend invitation',
        description: formatApiError(err),
        variant: 'destructive'
      });
    }
  });

  const sortedUsers = React.useMemo(() => {
    const list = usersData?.users ?? [];
    return [...list].sort((a, b) => a.email.localeCompare(b.email));
  }, [usersData?.users]);

  const filteredUsers = React.useMemo(() => {
    if (memberFilter === 'all') return sortedUsers;
    return sortedUsers.filter((u) => u.deactivatedAt == null);
  }, [memberFilter, sortedUsers]);

  const pendingInvites = React.useMemo(() => {
    const list = invitesData?.invites ?? [];
    return list
      .filter(isPendingInvitation)
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [invitesData?.invites]);

  const groupNameById = React.useMemo(
    () => new Map((groupsData?.groups ?? []).map((g) => [g.id, g.name])),
    [groupsData?.groups]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Users</h2>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="min-w-0 space-y-1.5">
            <CardTitle>Members</CardTitle>
            <CardDescription>
              {isOwner
                ? 'Everyone with access to this organization. Owners can change organization roles.'
                : 'Everyone with access to this organization. Only an owner can change organization roles.'}
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isOwner ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => setInviteStaffOpen(true)}
              >
                <UserPlus className="h-4 w-4" />
                Invite admin
              </Button>
            ) : null}
            {canInviteToRoom ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => setInviteRoomOpen(true)}
              >
                <Mail className="h-4 w-4" />
                Invite viewer
              </Button>
            ) : null}
            <div className="ml-auto">
              <Select
                value={memberFilter}
                onValueChange={(value) =>
                  setMemberFilter(value as MemberFilter)
                }
              >
                <SelectTrigger className="w-[180px] shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="active">Active members</SelectItem>
                  <SelectItem value="all">All members</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {memberFilter === 'active' && sortedUsers.length > 0
                ? 'No active members match this filter.'
                : 'No users found.'}
            </p>
          ) : (
            <>
              <div className="divide-y rounded-md border lg:hidden">
                {filteredUsers.map((u) => {
                  const lastLogin = u.lastLoginAt
                    ? formatDateTime(u.lastLoginAt)
                    : null;
                  const archived = u.deactivatedAt != null;
                  return (
                    <div
                      key={u.id}
                      className={`space-y-3 px-4 py-4 ${archived ? 'opacity-60' : ''}`}
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <Avatar className="size-9 shrink-0 border bg-muted">
                          <AvatarFallback
                            delayMs={0}
                            className="text-xs font-semibold text-muted-foreground"
                          >
                            {initialsFromEmail(u.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">
                              {displayNameFromEmail(u.email)}
                            </span>
                            {u.id === user?.id ? (
                              <Badge
                                variant="secondary"
                                className="shrink-0 px-2 py-0 text-[11px]"
                              >
                                You
                              </Badge>
                            ) : null}
                            {archived ? (
                              <Badge
                                variant="outline"
                                className="shrink-0 px-2 py-0 text-[11px]"
                              >
                                Archived
                              </Badge>
                            ) : null}
                          </div>
                          <div className="break-all text-sm text-muted-foreground">
                            {u.email}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Last sign-in:{' '}
                            {lastLogin
                              ? `${lastLogin.date}, ${lastLogin.time}`
                              : 'Never'}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 pl-12">
                        <UserRoleControl
                          listedUser={u}
                          isOwner={isOwner}
                          currentUserId={user?.id}
                          archived={archived}
                          updateRolePending={updateRole.isPending}
                          onRoleChange={(role) =>
                            updateRole.mutate({ userId: u.id, role })
                          }
                        />
                        {isOwner ? (
                          <UserStatusControl
                            listedUser={u}
                            currentUserId={user?.id}
                            archived={archived}
                            deactivatePending={setDeactivated.isPending}
                            onReactivate={() => {
                              if (
                                window.confirm(
                                  `Reactivate ${u.email}? They will be able to sign in and download files again.`
                                )
                              ) {
                                setDeactivated.mutate({
                                  userId: u.id,
                                  deactivated: false
                                });
                              }
                            }}
                            onDeactivate={() => {
                              if (
                                window.confirm(
                                  `Deactivate ${u.email}? They will be archived and cannot sign in or download files.`
                                )
                              ) {
                                setDeactivated.mutate({
                                  userId: u.id,
                                  deactivated: true
                                });
                              }
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden rounded-md border lg:block">
                <Table>
                  <TableHeader className="bg-muted/40">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="px-4">Member</TableHead>
                      <TableHead className="px-4">Role</TableHead>
                      <TableHead className="px-4">Last sign-in</TableHead>
                      {isOwner ? (
                        <TableHead className="px-4 text-right">
                          Status
                        </TableHead>
                      ) : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => {
                      const lastLogin = u.lastLoginAt
                        ? formatDateTime(u.lastLoginAt)
                        : null;
                      const archived = u.deactivatedAt != null;
                      return (
                        <TableRow
                          key={u.id}
                          className={archived ? 'opacity-60' : undefined}
                        >
                          <TableCell className="min-w-0 px-4 py-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <Avatar className="size-9 shrink-0 border bg-muted">
                                <AvatarFallback
                                  delayMs={0}
                                  className="text-xs font-semibold text-muted-foreground"
                                >
                                  {initialsFromEmail(u.email)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="truncate font-medium text-foreground">
                                    {displayNameFromEmail(u.email)}
                                  </span>
                                  {u.id === user?.id ? (
                                    <Badge
                                      variant="secondary"
                                      className="shrink-0 px-2 py-0 text-[11px]"
                                    >
                                      You
                                    </Badge>
                                  ) : null}
                                  {archived ? (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 px-2 py-0 text-[11px]"
                                    >
                                      Archived
                                    </Badge>
                                  ) : null}
                                </div>
                                <div className="truncate text-sm text-muted-foreground">
                                  {u.email}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap px-4 py-3">
                            <UserRoleControl
                              listedUser={u}
                              isOwner={isOwner}
                              currentUserId={user?.id}
                              archived={archived}
                              updateRolePending={updateRole.isPending}
                              onRoleChange={(role) =>
                                updateRole.mutate({ userId: u.id, role })
                              }
                            />
                          </TableCell>
                          <TableCell className="whitespace-nowrap px-4 py-3">
                            {lastLogin ? (
                              <div className="leading-tight">
                                <div className="font-medium text-foreground">
                                  {lastLogin.date}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {lastLogin.time}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">
                                Never
                              </span>
                            )}
                          </TableCell>
                          {isOwner ? (
                            <TableCell className="px-4 py-3 text-right">
                              <UserStatusControl
                                listedUser={u}
                                currentUserId={user?.id}
                                archived={archived}
                                deactivatePending={setDeactivated.isPending}
                                onReactivate={() => {
                                  if (
                                    window.confirm(
                                      `Reactivate ${u.email}? They will be able to sign in and download files again.`
                                    )
                                  ) {
                                    setDeactivated.mutate({
                                      userId: u.id,
                                      deactivated: false
                                    });
                                  }
                                }}
                                onDeactivate={() => {
                                  if (
                                    window.confirm(
                                      `Deactivate ${u.email}? They will be archived and cannot sign in or download files.`
                                    )
                                  ) {
                                    setDeactivated.mutate({
                                      userId: u.id,
                                      deactivated: true
                                    });
                                  }
                                }}
                              />
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <CardTitle>Pending invitations</CardTitle>
          </div>
          <CardDescription>
            Unused invitations, including expired ones you can resend
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitesLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : pendingInvites.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No pending invitations.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="px-4">Email</TableHead>
                    <TableHead className="whitespace-nowrap px-4">
                      Type
                    </TableHead>
                    <TableHead className="hidden whitespace-nowrap px-4 md:table-cell">
                      Expires
                    </TableHead>
                    <TableHead className="whitespace-nowrap px-4 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvites.map((inv) => {
                    const expires = formatDateTime(inv.expiresAt);
                    const expired = isInviteExpired(inv);
                    return (
                      <TableRow
                        key={inv.id}
                        className={expired ? 'opacity-75' : undefined}
                      >
                        <TableCell className="px-4 py-3 font-medium">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span>{inv.email}</span>
                            {expired ? (
                              <Badge
                                variant="outline"
                                className="shrink-0 px-2 py-0 text-[11px] text-destructive"
                              >
                                Expired
                              </Badge>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <InviteTypeBadge
                            invite={inv}
                            groupNameById={groupNameById}
                          />
                        </TableCell>
                        <TableCell className="hidden px-4 py-3 md:table-cell">
                          {expires ? (
                            <div className="leading-tight">
                              <div
                                className={
                                  expired
                                    ? 'font-medium text-destructive'
                                    : 'font-medium text-foreground'
                                }
                              >
                                {expires.date}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {expires.time}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">
                              Unknown
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <div className="inline-flex items-center justify-end gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              disabled={
                                resendInvite.isPending || revokeInvite.isPending
                              }
                              onClick={() => resendInvite.mutate(inv.id)}
                            >
                              <RotateCcw className="h-4 w-4" aria-hidden />
                              <span className="sr-only">Resend invitation</span>
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              disabled={
                                resendInvite.isPending || revokeInvite.isPending
                              }
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Revoke invitation to ${inv.email}?`
                                  )
                                ) {
                                  revokeInvite.mutate(inv.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                              <span className="sr-only">Revoke invitation</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <InviteStaffModal
        open={inviteStaffOpen}
        onOpenChange={setInviteStaffOpen}
      />
      <InviteRoomViewerModal
        open={inviteRoomOpen}
        onOpenChange={setInviteRoomOpen}
      />
      <Toast
        open={roleErrorToast != null}
        onOpenChange={(open) => {
          if (!open) setRoleErrorToast(null);
        }}
        title="Couldn’t update user"
        description={roleErrorToast ?? undefined}
        variant="destructive"
      />
      <Toast
        open={inviteToast != null}
        onOpenChange={(open) => {
          if (!open) setInviteToast(null);
        }}
        title={inviteToast?.title ?? ''}
        description={inviteToast?.description}
        variant={inviteToast?.variant}
      />
    </div>
  );
}
